import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  LinearProgress,
} from '@mui/material';
import {
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useWebMapContext } from '../webMap/WebMapContext';
import type { WidgetDataBinding, LiveStatusWidgetConfig } from './types';

interface GroupStat {
  label: string;
  total: number;
  active: number;
}

interface Props {
  widgetConfig?: LiveStatusWidgetConfig & { dataBinding?: WidgetDataBinding };
}

const LiveStatusWidget: React.FC<Props> = ({ widgetConfig }) => {
  const ctx = useWebMapContext();

  const groupByField = widgetConfig?.groupByField || 'device_type';
  const tsField = widgetConfig?.timestampField || 'timestamp';
  const activeThreshold = widgetConfig?.activeThresholdMinutes ?? 30;
  const statFields = widgetConfig?.statFields ?? [];

  const features = useMemo(() => {
    const layerId = widgetConfig?.dataBinding?.boundLayerId;
    if (!layerId || !ctx) return [];
    return ctx.getFeaturesForLayer(layerId);
  }, [widgetConfig?.dataBinding?.boundLayerId, ctx]);

  const { groups, totalCount, activeCount, aggregates } = useMemo(() => {
    const now = Date.now();
    const thresholdMs = activeThreshold * 60000;
    const groupMap = new Map<string, { total: number; active: number }>();
    let total = 0;
    let active = 0;

    const aggSums: Record<string, number> = {};
    const aggCounts: Record<string, number> = {};
    for (const sf of statFields) {
      aggSums[sf] = 0;
      aggCounts[sf] = 0;
    }

    for (const f of features) {
      const p = f.properties ?? {};
      total++;

      const tsVal = p[tsField];
      const isActive = tsVal != null && (now - new Date(String(tsVal)).getTime()) < thresholdMs;
      if (isActive) active++;

      const group = String(p[groupByField] ?? 'Unknown');
      const entry = groupMap.get(group) ?? { total: 0, active: 0 };
      entry.total++;
      if (isActive) entry.active++;
      groupMap.set(group, entry);

      for (const sf of statFields) {
        const v = Number(p[sf]);
        if (Number.isFinite(v)) {
          aggSums[sf] += v;
          aggCounts[sf]++;
        }
      }
    }

    const groups: GroupStat[] = [];
    groupMap.forEach((v, label) => {
      groups.push({ label, ...v });
    });
    groups.sort((a, b) => b.total - a.total);

    const aggregates = statFields.map((sf) => ({
      field: sf,
      avg: aggCounts[sf] > 0 ? aggSums[sf] / aggCounts[sf] : 0,
      sum: aggSums[sf],
      count: aggCounts[sf],
    }));

    return { groups, totalCount: total, activeCount: active, aggregates };
  }, [features, groupByField, tsField, activeThreshold, statFields]);

  if (!widgetConfig?.dataBinding?.boundLayerId) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Bind this widget to a Web Map layer to show live status.
        </Typography>
      </Box>
    );
  }

  const activePct = totalCount > 0 ? (activeCount / totalCount) * 100 : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto', p: 1.5 }}>
      {/* Overall summary */}
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Overall Status
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {totalCount}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 0.75 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DotIcon sx={{ fontSize: 10, color: '#4caf50' }} />
              <Typography variant="caption">Active: <strong>{activeCount}</strong></Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DotIcon sx={{ fontSize: 10, color: '#9e9e9e' }} />
              <Typography variant="caption">Inactive: <strong>{totalCount - activeCount}</strong></Typography>
            </Stack>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={activePct}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: '#4caf50', borderRadius: 3 },
            }}
          />
        </CardContent>
      </Card>

      {/* Group breakdown */}
      {groups.length > 0 && (
        <Stack spacing={1}>
          {groups.map((g) => {
            const pct = g.total > 0 ? (g.active / g.total) * 100 : 0;
            return (
              <Card key={g.label} variant="outlined">
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {g.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {g.active}/{g.total}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                        bgcolor: pct > 80 ? '#4caf50' : pct > 40 ? '#ff9800' : '#f44336',
                      },
                    }}
                  />
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Aggregated stat fields */}
      {aggregates.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Field Summaries
          </Typography>
          {aggregates.map((a) => (
            <Stack key={a.field} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {a.field}
              </Typography>
              <Typography variant="caption">
                avg <strong>{a.avg.toFixed(1)}</strong> &middot; sum <strong>{a.sum.toLocaleString()}</strong> &middot; n={a.count}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default LiveStatusWidget;
